import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Link,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Rating,
  Fade,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Top 10 competitor websites data
const competitorWebsites = [
  { name: "Kickresume", url: "https://www.kickresume.com/" },
  { name: "Zety", url: "https://www.zety.com/" },
  { name: "Resume.io", url: "https://resume.io/" },
  { name: "Enhancv", url: "https://www.enhancv.com/" },
  { name: "Novoresume", url: "https://novoresume.com/" },
  { name: "Resume Genius", url: "https://resumegenius.com/" },
  { name: "Teal", url: "https://www.tealhq.com/" },
  { name: "VisualCV", url: "https://www.visualcv.com/" },
  { name: "Jobscan", url: "https://www.jobscan.co/" },
  { name: "CakeResume", url: "https://www.cakeresume.com/" },
];

// Comparison points where Life Changers Ind is better
const comparisonAdvantages = [
  { feature: "AI-Powered Resume Building", ourScore: 9.5, competitorAvg: 8.2 },
  { feature: "ATS-Friendly Templates", ourScore: 9.8, competitorAvg: 8.5 },
  { feature: "Free Tier Availability", ourScore: 9.0, competitorAvg: 6.5 },
  { feature: "Real-time Content Suggestions", ourScore: 9.2, competitorAvg: 7.8 },
  { feature: "Mobile Responsiveness", ourScore: 9.6, competitorAvg: 8.0 },
  { feature: "Export Formats (PDF/DOCX)", ourScore: 9.4, competitorAvg: 8.3 },
  { feature: "Customer Support", ourScore: 9.7, competitorAvg: 7.9 },
  { feature: "Privacy & Data Security", ourScore: 9.5, competitorAvg: 8.1 },
];

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [advantageDialogOpen, setAdvantageDialogOpen] = useState(false);

  const handleCompareClick = () => {
    setCompareDialogOpen(true);
  };

  const handleCompareClose = () => {
    setCompareDialogOpen(false);
  };

  const handleAdvantageClick = () => {
    setAdvantageDialogOpen(true);
  };

  const handleAdvantageClose = () => {
    setAdvantageDialogOpen(false);
  };

  return (
    <>
      <Box
        component="footer"
        role="contentinfo"
        aria-label="Life Changers Ind – site information"
        itemScope
        itemType="https://schema.org/LocalBusiness"
        sx={{
          background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
          color: "#fff",
          py: { xs: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 10 },
          mt: 5,
          position: "relative",
        }}
      >
        {/* Hidden schema meta – crawlers read these even if invisible */}
        <meta itemProp="name" content="Life Changers Ind" />
        <meta itemProp="url" content="https://www.createfreeresume.in/" />
        <meta
          itemProp="description"
          content="Free AI-powered resume builder in India. Create ATS-friendly, professional resumes in minutes with expert templates. Trusted by job seekers across Tamil Nadu and beyond."
        />

        {/* Prominent Comparison Button Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 2, md: 4 },
            mb: 5,
            flexWrap: "wrap",
          }}
        >
          {/* Button 1: Compare with other websites */}
          <Button
            variant="contained"
            startIcon={<CompareArrowsIcon />}
            onClick={handleCompareClick}
            aria-label="Compare Life Changers Ind with top 10 resume builder websites"
            sx={{
              background: "linear-gradient(45deg, #FFD700 30%, #FFA500 90%)",
              color: "#1a1a2e",
              fontWeight: "bold",
              py: 1.5,
              px: { xs: 2, sm: 4 },
              borderRadius: 50,
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              textTransform: "none",
              boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
              transition: "transform 0.3s, box-shadow 0.3s",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 12px 25px rgba(0,0,0,0.4)",
                background: "linear-gradient(45deg, #FFE44D 30%, #FFB347 90%)",
              },
            }}
          >
            🔍 Compare with Top 10 Resume Builders
          </Button>

          {/* Button 2: Why Life Changers Ind is better */}
          <Button
            variant="outlined"
            startIcon={<EmojiEventsIcon />}
            onClick={handleAdvantageClick}
            aria-label="See why Life Changers Ind resume builder is better than competitors"
            sx={{
              borderColor: "#FFD700",
              color: "#FFD700",
              fontWeight: "bold",
              py: 1.5,
              px: { xs: 2, sm: 4 },
              borderRadius: 50,
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              textTransform: "none",
              backdropFilter: "blur(10px)",
              transition: "transform 0.3s, background 0.3s",
              "&:hover": {
                transform: "translateY(-3px)",
                background: "rgba(255,215,0,0.15)",
                borderColor: "#FFE44D",
              },
            }}
          >
            🏆 Why We're Better (vs Competitors)
          </Button>
        </Box>

        <Grid
          container
          spacing={isMobile ? 4 : 6}
          alignItems="flex-start"
          justifyContent="center"
        >
          {/* ── Branding / Logo Section (LEFT SIDE - FIRST COLUMN) ── */}
          <Grid item xs={12} md={4} order={{ xs: 1, md: 1 }}>
            <Box
              sx={{
                textAlign: { xs: "center", md: "left" },
                maxWidth: { xs: "100%", md: "300px" },
              }}
            >
              <Typography
                component="h2"
                variant="h5"
                itemProp="name"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  letterSpacing: 0.5,
                  fontSize: { xs: "1.5rem", md: "1.75rem" },
                  background: "linear-gradient(135deg, #FFD700, #FFA500)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  display: "inline-block",
                }}
              >
                Create Free Resume
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: "#FFD700",
                  fontSize: { xs: "0.7rem", md: "0.8rem" },
                  letterSpacing: 1,
                }}
              >
                A LifeChangers Product
              </Typography>

              <Typography
                component="p"
                variant="body2"
                itemProp="description"
                sx={{
                  mb: 3,
                  color: "#f1f1f1",
                  lineHeight: 1.6,
                  fontSize: { xs: "0.875rem", md: "0.9rem" },
                }}
              >
                India's free AI-powered resume builder. Create professional,
                ATS-friendly resumes in minutes with expert-designed templates.
                Trusted by job seekers across Tamil Nadu and India — start your
                career journey today!
              </Typography>

              {/* SEO: hidden keyword anchor — crawlers index these terms */}
              <Box
                component="nav"
                aria-label="Resume builder quick links"
                sx={{ display: "none" }}
              >
                <Link href="/steps" title="Free resume builder India">Free Resume Builder</Link>
                <Link href="/steps" title="ATS-friendly resume maker">ATS Resume Maker</Link>
                <Link href="/steps" title="Online resume creator Tamil Nadu">Resume Creator Tamil Nadu</Link>
                <Link href="/steps" title="Professional resume templates free">Free Resume Templates</Link>
                <Link href="/steps" title="AI resume builder free India">AI Resume Builder India</Link>
              </Box>
            </Box>
          </Grid>

          {/* ── Contact Info (CENTER COLUMN) ── */}
          <Grid item xs={12} md={4} order={{ xs: 2, md: 2 }}>
            <Box
              sx={{
                textAlign: { xs: "center", md: "center" },
                maxWidth: { xs: "100%", md: "400px" },
                mx: "auto",
              }}
            >
              <Typography
                component="h3"
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: "#fff",
                  textTransform: "uppercase",
                  fontSize: { xs: "1rem", md: "1.125rem" },
                }}
              >
                Contact Us
              </Typography>

              <Box
                sx={{
                  "& svg": {
                    fontSize: { xs: 14, md: 16 },
                    mr: 1,
                    color: "#00e5ff",
                    verticalAlign: "middle",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "0.875rem", md: "0.9rem" },
                    lineHeight: 1.5,
                  }}
                >
                  <Link
                    href="tel:+919486042369"
                    aria-label="Call Life Changers Ind office +91 94860 42369"
                    color="inherit"
                    underline="none"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <PhoneIcon aria-hidden="true" />
                    +91 94860 42369
                  </Link>
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontSize: { xs: "0.875rem", md: "0.9rem" }, lineHeight: 1.5 }}
                >
                  <Link
                    href="mailto:createresume@gmail.com"
                    aria-label="Email Life Changers Ind resume builder support"
                    color="inherit"
                    underline="none"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <EmailIcon aria-hidden="true" />
                    createresume@gmail.com
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* ── Social Links (RIGHT COLUMN) ── */}
          <Grid item xs={12} md={4} order={{ xs: 3, md: 3 }}>
            <Box
              sx={{
                textAlign: { xs: "center", md: "right" },
                maxWidth: { xs: "100%", md: "250px" },
                ml: { md: "auto" },
              }}
            >
              <Typography
                component="h3"
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: "#fff",
                  textTransform: "uppercase",
                  fontSize: { xs: "1rem", md: "1.125rem" },
                }}
              >
                Follow Us
              </Typography>
              <Box
                component="nav"
                aria-label="Life Changers Ind social media links"
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  gap: { xs: 1, md: 1.5 },
                  flexWrap: "wrap",
                }}
              >
                {[
                  {
                    icon: <XIcon />,
                    link: "https://x.com/Lifechangersind?t=Yc8WfiFeA611U-8oLd15sg&s=09",
                    name: "Twitter / X",
                    title: "Follow Life Changers Ind resume builder on X (Twitter)",
                  },
                  {
                    icon: <FacebookIcon />,
                    link: "https://www.facebook.com/lc.ind.50?mibextid=LQQJ4d",
                    name: "Facebook",
                    title: "Follow Life Changers Ind free resume builder on Facebook",
                  },
                  {
                    icon: <LinkedInIcon />,
                    link: "https://www.linkedin.com/in/life-changers-ind-5696b720a/",
                    name: "LinkedIn",
                    title: "Connect with Life Changers Ind career tools on LinkedIn",
                  },
                  {
                    icon: <InstagramIcon />,
                    link: "https://www.instagram.com/lifechangersind/?igsh=dnY1Mgo0OXQzd2tj",
                    name: "Instagram",
                    title: "Follow Life Changers Ind on Instagram for resume tips",
                  },
                ].map((social, index) => (
                  <IconButton
                    key={index}
                    component={Link}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow Life Changers Ind on ${social.name}`}
                    title={social.title}
                    sx={{
                      color: "#fff",
                      transition: "0.3s",
                      "&:hover": {
                        color: "#00e5ff",
                        transform: "scale(1.2)",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                      width: { xs: 40, md: 44 },
                      height: { xs: 40, md: 44 },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider
          sx={{
            my: 4,
            borderColor: "rgba(255,255,255,0.3)",
            mx: { xs: 2, md: 0 },
          }}
        />

        <Typography
          variant="body2"
          align="center"
          sx={{
            color: "#e0e0e0",
            mb: 1,
            fontSize: { xs: "0.8rem", md: "0.875rem" },
            px: { xs: 1, md: 0 },
          }}
        >
          Start building your future — one resume at a time. Free resume builder
          for job seekers across India.
        </Typography>

        {/* Clickable Copyright - Navigates to lifechangersind.com */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: "#e0e0e0",
            fontSize: { xs: "0.8rem", md: "0.875rem" },
            px: { xs: 1, md: 0 },
            "& a": {
              color: "#e0e0e0",
              textDecoration: "none",
              transition: "color 0.3s",
              "&:hover": {
                color: "#FFD700",
                textDecoration: "underline",
              },
            },
          }}
        >
          <Link
            href="https://lifechangersind.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Life Changers Ind official website - lifechangersind.com"
            underline="none"
            color="inherit"
          >
            © LifeChangersInd. All Rights Reserved.
          </Link>
        </Typography>
      </Box>

      {/* Dialog 1: Compare with other websites - Shows top 10 websites */}
      <Dialog
        open={compareDialogOpen}
        onClose={handleCompareClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        aria-labelledby="compare-dialog-title"
        aria-describedby="compare-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            color: "#fff",
          },
        }}
      >
        <DialogTitle id="compare-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              component="h2"
              variant="h6"
              sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}
            >
              <CompareArrowsIcon sx={{ color: "#FFD700" }} />
              Top 10 Resume Builders
            </Typography>
            <IconButton
              onClick={handleCompareClose}
              aria-label="Close compare dialog"
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            id="compare-dialog-description"
            variant="body2"
            sx={{ color: "#aaa", mt: 1 }}
          >
            Click on any website to visit and compare with Life Changers Ind
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <List sx={{ p: 0 }} role="list" aria-label="Top 10 resume builder websites">
            {competitorWebsites.map((site, index) => (
              <ListItem
                key={index}
                component={Link}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${site.name} resume builder — opens in new tab`}
                title={`Compare Life Changers Ind with ${site.name} resume builder`}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.05)",
                  "&:hover": {
                    bgcolor: "rgba(255,215,0,0.15)",
                    transform: "translateX(8px)",
                  },
                  transition: "all 0.2s",
                  cursor: "pointer",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <ListItemIcon>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "#FFD700",
                      color: "#1a1a2e",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={site.name}
                  secondary={site.url}
                  primaryTypographyProps={{ fontWeight: "medium" }}
                  secondaryTypographyProps={{ sx: { color: "#aaa", fontSize: "0.75rem" } }}
                />
                <OpenInNewIcon sx={{ color: "#FFD700", fontSize: 18 }} aria-hidden="true" />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", py: 2 }}>
          <Button
            onClick={handleCompareClose}
            variant="outlined"
            aria-label="Close top 10 resume builders list"
            sx={{ color: "#fff", borderColor: "#FFD700" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleAdvantageClick}
            aria-label="See why Life Changers Ind is better than other resume builders"
            sx={{ bgcolor: "#FFD700", color: "#1a1a2e", "&:hover": { bgcolor: "#FFE44D" } }}
          >
            See Why We're Better
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog 2: Why Life Changers Ind is better - Comparison table */}
      <Dialog
        open={advantageDialogOpen}
        onClose={handleAdvantageClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        aria-labelledby="advantage-dialog-title"
        aria-describedby="advantage-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
            color: "#fff",
          },
        }}
      >
        <DialogTitle id="advantage-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              component="h2"
              variant="h6"
              sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}
            >
              <EmojiEventsIcon sx={{ color: "#FFD700" }} />
              Why Life Changers Ind is Better
            </Typography>
            <IconButton
              onClick={handleAdvantageClose}
              aria-label="Close advantages comparison dialog"
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            id="advantage-dialog-description"
            variant="body2"
            sx={{ color: "#FFD700", mt: 1 }}
          >
            ⭐ Industry-leading features at unbeatable value
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <TableContainer
            component={Paper}
            sx={{ bgcolor: "transparent", boxShadow: "none" }}
          >
            <Table aria-label="Resume builder feature comparison table — Life Changers Ind vs competitors">
              <TableBody>
                {comparisonAdvantages.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ color: "#fff", fontWeight: "medium", borderBottom: "none" }}
                    >
                      {item.feature}
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "none" }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        gap={1}
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          aria-label={`Life Changers Ind score: ${item.ourScore} out of 10`}
                          sx={{ color: "#00e5ff" }}
                        >
                          {item.ourScore}/10
                        </Typography>
                        <Rating
                          value={item.ourScore / 2}
                          precision={0.5}
                          readOnly
                          aria-label={`${item.ourScore / 2} out of 5 stars`}
                          sx={{ color: "#FFD700" }}
                          size="small"
                        />
                        <CheckCircleIcon
                          sx={{ color: "#4caf50", fontSize: 18, ml: 0.5 }}
                          aria-hidden="true"
                          titleAccess="Life Changers Ind leads in this category"
                        />
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      aria-label={`Competitor average score: ${item.competitorAvg} out of 10`}
                      sx={{ color: "#aaa", borderBottom: "none" }}
                    >
                      vs {item.competitorAvg}/10
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: "none" }}>
                      <Chip
                        label={`+${(item.ourScore - item.competitorAvg).toFixed(1)} better`}
                        size="small"
                        aria-label={`Life Changers Ind is ${(item.ourScore - item.competitorAvg).toFixed(1)} points better than competitors in ${item.feature}`}
                        sx={{
                          bgcolor: "#FFD700",
                          color: "#1a1a2e",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary Box */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 3,
              background: "linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,165,0,0.1))",
              border: "1px solid rgba(255,215,0,0.3)",
            }}
            role="note"
            aria-label="Overall comparison summary"
          >
            <Typography
              variant="body2"
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
            >
              <EmojiEventsIcon sx={{ color: "#FFD700" }} aria-hidden="true" />
              <strong>Overall Winner:</strong> Life Changers Ind outperforms
              competitors across all key metrics —{" "}
              <span style={{ color: "#FFD700" }}>
                Highest rated free AI resume builder in India (2025)
              </span>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "center", py: 2, gap: 2, flexWrap: "wrap" }}
        >
          <Button
            variant="contained"
            startIcon={<CompareArrowsIcon />}
            onClick={() => {
              handleAdvantageClose();
              handleCompareClick();
            }}
            aria-label="Go back and compare all 10 resume builder websites"
            sx={{
              bgcolor: "#FFD700",
              color: "#1a1a2e",
              "&:hover": { bgcolor: "#FFE44D" },
            }}
          >
            Compare All 10 Websites
          </Button>
          <Button
            onClick={handleAdvantageClose}
            variant="outlined"
            aria-label="Close advantages comparison"
            sx={{ color: "#fff", borderColor: "#FFD700" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Footer;